import {
    createDisposer,
    NetworkStateImpl,
    networkState,
    type Unsubscribe,
} from '@amazon/vinyl-util'
import { ctx } from '@/util/context'
import { windowEvents } from '@/util/interaction'

/** The OS's network state, as the desktop shell's `network.rs` reports it. */
export interface ShellNetwork {
    /** Whether the OS has a usable path to a network. */
    readonly satisfied: boolean
    /** Whether that network reaches the internet; null where the OS doesn't say (macOS). */
    readonly internet: boolean | null
}

/** The shell's network monitor. An older native build has none: its `state` rejects. */
export interface NetworkShell {
    /** The state the OS reported last; null before its first report. */
    state(): Promise<ShellNetwork | null>
    onChange(listener: (network: ShellNetwork) => void): Promise<Unsubscribe>
}

const NETWORK_EVENT = 'network-state'

export const tauriNetworkShell: NetworkShell = {
    async state() {
        const { invoke } = await import('@tauri-apps/api/core')
        return invoke<ShellNetwork | null>('network_state')
    },
    async onChange(listener) {
        const { listen } = await import('@tauri-apps/api/event')
        return listen<ShellNetwork>(NETWORK_EVENT, (event) => listener(event.payload))
    },
}

const { errorHandler } = ctx('network')

const reachable = (network: ShellNetwork) => network.satisfied && network.internet !== false

/**
 * Makes the app's network state (`getNetworkState()`, `onLine()`) follow the shell's monitor, as
 * an `online` or `offline` event on `window` at each change. The webview's `navigator.onLine` and
 * its own events stand until the monitor's first report, and for good on a native build without
 * one. Call before anything reads the network state.
 */
export function installShellNetworkState(
    shell: NetworkShell = tauriNetworkShell,
    webview: { readonly onLine: boolean } = window.navigator
): Unsubscribe {
    const disposer = createDisposer()
    const { add } = disposer
    let reported: boolean | null = null
    const shellNavigator = {
        get onLine() {
            return reported ?? webview.onLine
        },
    }
    networkState.set(() => new NetworkStateImpl({ eventTarget: window, navigator: shellNavigator }))
    add(() => networkState.reset())

    // The webview's own events lag the monitor's or contradict them; untrusted ones are ours.
    const dropWebviewEvent = (event: Event) => {
        if (reported !== null && event.isTrusted) event.stopImmediatePropagation()
    }
    add(windowEvents.on('online', dropWebviewEvent, { capture: true }))
    add(windowEvents.on('offline', dropWebviewEvent, { capture: true }))

    const unreported = () => reported === null && !disposer.disposed
    const follow = (network: ShellNetwork) => {
        const was = shellNavigator.onLine
        reported = reachable(network)
        if (reported !== was) window.dispatchEvent(new Event(reported ? 'online' : 'offline'))
    }
    ;(async () => {
        const unlisten = await shell.onChange(follow)
        if (disposer.disposed) return unlisten()
        add(unlisten)
        // An older native build has no `network_state`; the webview's state stands there.
        const network = await shell.state().catch(() => null)
        if (network && unreported()) follow(network)
    })().catch(errorHandler)
    return disposer.dispose
}
